import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '@mm-aggregator/drf-dynamics';

export interface Paged<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface BatchProgress {
  id: string;
  status: string;
  total_count: number;
  treated_count: number;
  processed_count: number;
  success_count: number;
  failed_count: number;
  progress_percentage: number;
  line_statuses: Record<string, number>;
  celery_task_id: string;
  celery_state: string | null;
  completed_at: string | null;
}

export interface BatchLine {
  id: string;
  line_index: number;
  destination_account: string;
  destination_name: string;
  amount: string;
  merchant_reference: string;
  status: { value?: string; title?: string; css?: string } | string;
  comment: string;
  error_message: string;
  payout: string | null;
}

/** Back-office actions on settlements and bulk disbursement batches (under ``records/``). */
@Injectable({ providedIn: 'root' })
export class RecordsOpsService {
  private readonly api = inject(ApiService);

  // ---- Settlements ----
  getSettlement(id: string): Observable<Record<string, unknown>> {
    return this.api.get<Record<string, unknown>>(`records/settlements/${id}/`);
  }

  markSettlementPaid(
    id: string,
    body: { external_reference: string; notes: string; proof_file?: File | null },
  ): Observable<{ detail: string; status: string }> {
    const payload: Record<string, unknown> = {
      external_reference: body.external_reference,
      notes: body.notes,
    };
    if (body.proof_file) {
      payload['proof_file'] = body.proof_file;
    }
    return this.api.post(`records/settlements/${id}/mark-paid/`, payload);
  }

  markSettlementFailed(id: string, reason: string): Observable<{ detail: string; status: string }> {
    return this.api.post(`records/settlements/${id}/mark-failed/`, { reason });
  }

  // ---- Batches ----
  batchProgress(id: string): Observable<BatchProgress> {
    return this.api.get<BatchProgress>(`records/batch-disbursements/${id}/progress/`);
  }

  batchLines(id: string, page = 1): Observable<Paged<BatchLine>> {
    return this.api.get<Paged<BatchLine>>(`records/batch-disbursements/${id}/lines/`, { page });
  }

  batchPayouts(id: string, page = 1): Observable<Paged<Record<string, unknown>>> {
    return this.api.get<Paged<Record<string, unknown>>>(
      `records/batch-disbursements/${id}/payouts/`,
      { page },
    );
  }

  batchResult(id: string): Observable<Blob> {
    return this.api.getBlob(`records/batch-disbursements/${id}/result/`);
  }

  cancelBatch(id: string): Observable<{ cancelled_lines: number; status: string }> {
    return this.api.post(`records/batch-disbursements/${id}/cancel/`, {});
  }

  retryBatch(id: string): Observable<{ requeued_lines: number; status: string }> {
    return this.api.post(`records/batch-disbursements/${id}/retry/`, {});
  }
}
